import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { getSocket, joinThread, leaveThread, onSocketReady } from '../lib/socket';
import type { Contribution, NarrativeReadyEvent, Reaction, SphereBalanceEvent } from '../api/types';

/** A reaction was added by anyone in the thread room (`reaction_added`). */
export interface ReactionAddedEvent {
  contributionId: string;
  reactionType: string;
  userId: string | null;
  guestSessionId?: string | null;
}

/** A reaction was removed by anyone in the thread room (`reaction_removed`). */
export interface ReactionRemovedEvent {
  contributionId: string;
  reactionType: string;
  userId: string | null;
}

export interface PresenceEntry {
  userId: string;
  displayName: string;
  status: 'viewing' | 'composing:text' | 'composing:photo' | 'composing:audio' | 'composing:video';
  since: string;
}

export interface PhaseRecapReadyEvent {
  threadId: string;
  phaseIndex: number;
  phaseName: string;
  energyBand: string;
}

export interface PhaseRecapTTSReadyEvent {
  threadId: string;
  phaseIndex: number;
  ttsAudioUrl: string;
  ttsDurationSeconds: number;
}

/**
 * Completion of the AI phase-recap enhancement job. `aiEnhanced=false` means the
 * job ran but declined to rewrite (circuit breaker, provider error, no
 * contributions) — the original summary stands. Pages use this both to swap in
 * the polished summary and to clear their in-flight cue.
 */
export interface PhaseRecapEnhancedEvent {
  threadId: string;
  phaseIndex: number;
  phaseName: string;
  aiEnhanced: boolean;
}

/**
 * Adaptive (story-tailored) prompts finished generating for a phase.
 * `isAdapted=false` means no tailoring is coming — the default prompts stand, so
 * the contributor page can stop showing any "shaping" cue without a refetch.
 */
export interface AdaptivePromptsReadyEvent {
  threadId: string;
  phaseIndex: number;
  trigger: string;
  isAdapted: boolean;
}

/** Completion of the AI narrative reordering/transition job. */
export interface NarrativeEnhancedEvent {
  threadId: string;
  recapId: string;
  aiEnhanced: boolean;
  transitionsGenerated?: number;
  reorderingMovesApplied?: number;
  coherenceScore?: number | null;
}

/** A Story Finale deliverable finished (or failed) generating. */
export interface DeliverableReadyEvent {
  threadId: string;
  deliverableId: string;
  type: string;
  status: 'completed' | 'failed';
  ttsStatus?: string;
}

/** A recap/Highlight reel finished rendering to S3. */
export interface RecapRenderCompleteEvent {
  threadId: string;
  recapId: string;
  /**
   * Handle to the rendered reel, resolved by the public player at `/r/:token`.
   * `recapId` alone reaches nothing a client can play. Optional: recaps minted
   * before the token was carried on this event won't have one, so anything
   * routing off it needs a destination for the null case.
   */
  shareToken?: string | null;
  status: string;
  variants: string[];
  watermarked: boolean;
}

/** A recap/Highlight reel render failed terminally (after retries). */
export interface RecapRenderFailedEvent {
  threadId: string;
  recapId: string;
  error: string;
}

export interface DeliverableTTSReadyEvent {
  threadId: string;
  deliverableId: string;
  ttsAudioUrl: string;
  ttsDurationSeconds: number;
}

/** CAPTURE_FIRST_STORY_FLOW — a live banter message (guest or crew). */
export interface LiveChatMessage {
  id: string;
  authorDisplayName: string;
  colorSeed: string;
  content: string;
  isGuest: boolean;
}

/** Upper bound on the retained banter stream so a long session can't grow it without limit. */
const LIVE_MESSAGE_CAP = 200;

/** CAPTURE_FIRST_STORY_FLOW — a transient floating reaction to spawn. */
export interface LiveReactionPing {
  seed: string;
  emoji: string;
  /** Monotonic key so each event spawns a fresh float, even for the same emoji. */
  key: number;
}

interface ThreadEventsResult {
  liveContributions: Contribution[];
  /** CAPTURE_FIRST_STORY_FLOW — the live banter stream, in arrival order. */
  liveMessages: LiveChatMessage[];
  /** CAPTURE_FIRST_STORY_FLOW — the most recent reaction ping (transient). */
  liveReaction: LiveReactionPing | null;
  /** CAPTURE_FIRST_STORY_FLOW — active live-session id, learned from live:* events. */
  liveSessionId: string | null;
  presence: PresenceEntry[];
  latestAuthor: string | null;
  clearLatestAuthor: () => void;
  phaseRecapReady: PhaseRecapReadyEvent | null;
  clearPhaseRecapReady: () => void;
  narrativeReady: NarrativeReadyEvent | null;
  clearNarrativeReady: () => void;
  phaseRecapTTSReady: PhaseRecapTTSReadyEvent | null;
  clearPhaseRecapTTSReady: () => void;
  /** AI-polished phase-recap summary arrived (or was declined). */
  phaseRecapEnhanced: PhaseRecapEnhancedEvent | null;
  clearPhaseRecapEnhanced: () => void;
  /** Story-tailored prompts finished generating for a phase. */
  adaptivePromptsReady: AdaptivePromptsReadyEvent | null;
  clearAdaptivePromptsReady: () => void;
  /** AI narrative weave finished (or was declined). */
  narrativeEnhanced: NarrativeEnhancedEvent | null;
  clearNarrativeEnhanced: () => void;
  deliverableTTSReady: DeliverableTTSReadyEvent | null;
  clearDeliverableTTSReady: () => void;
  /** A Story Finale deliverable finished (or failed) generating. */
  deliverableReady: DeliverableReadyEvent | null;
  clearDeliverableReady: () => void;
  /** A recap/Highlight reel finished rendering. */
  recapRenderComplete: RecapRenderCompleteEvent | null;
  clearRecapRenderComplete: () => void;
  /** A recap/Highlight reel render failed terminally. */
  recapRenderFailed: RecapRenderFailedEvent | null;
  clearRecapRenderFailed: () => void;
  /**
   * Advisory gap nudge for role-based templates (Plot Armor). Points at the
   * spheres of action that still have no voice — never blocks, never implies a
   * wrong choice. Surfaced as a gentle, dismissible relatedness invitation.
   */
  sphereBalance: SphereBalanceEvent | null;
  clearSphereBalance: () => void;
}

interface UseThreadEventsOptions {
  /**
   * Called when template turn/phase state has changed and page-local prompt
   * context such as Hot Seat `isMyTurn` should be refreshed.
   */
  onTurnContextInvalidated?: () => void;
  /**
   * Called when anyone in the thread room reacts. The page owns the reaction
   * summary state, so it reconciles counts here (and suppresses its own
   * optimistic echo). Callback-based (like onTurnContextInvalidated) so rapid
   * events can't be dropped by a single-slot state + clear.
   */
  onReactionAdded?: (event: ReactionAddedEvent) => void;
  /** Called when anyone in the thread room un-reacts. */
  onReactionRemoved?: (event: ReactionRemovedEvent) => void;
}

export function useThreadEvents(
  threadId: string | undefined,
  options: UseThreadEventsOptions = {},
): ThreadEventsResult {
  const [liveContributions, setLiveContributions] = useState<Contribution[]>([]);
  const [liveMessages, setLiveMessages] = useState<LiveChatMessage[]>([]);
  const [liveReaction, setLiveReaction] = useState<LiveReactionPing | null>(null);
  const [liveSessionId, setLiveSessionId] = useState<string | null>(null);
  const reactionKeyRef = useRef(0);
  const [presence, setPresence] = useState<PresenceEntry[]>([]);
  const [latestAuthor, setLatestAuthor] = useState<string | null>(null);
  const [phaseRecapReady, setPhaseRecapReady] = useState<PhaseRecapReadyEvent | null>(null);
  const [narrativeReady, setNarrativeReady] = useState<NarrativeReadyEvent | null>(null);
  const [phaseRecapTTSReady, setPhaseRecapTTSReady] = useState<PhaseRecapTTSReadyEvent | null>(null);
  const [phaseRecapEnhanced, setPhaseRecapEnhanced] = useState<PhaseRecapEnhancedEvent | null>(null);
  const [adaptivePromptsReady, setAdaptivePromptsReady] = useState<AdaptivePromptsReadyEvent | null>(null);
  const [narrativeEnhanced, setNarrativeEnhanced] = useState<NarrativeEnhancedEvent | null>(null);
  const [deliverableTTSReady, setDeliverableTTSReady] = useState<DeliverableTTSReadyEvent | null>(null);
  const [deliverableReady, setDeliverableReady] = useState<DeliverableReadyEvent | null>(null);
  const [recapRenderComplete, setRecapRenderComplete] = useState<RecapRenderCompleteEvent | null>(null);
  const [recapRenderFailed, setRecapRenderFailed] = useState<RecapRenderFailedEvent | null>(null);
  const [sphereBalance, setSphereBalance] = useState<SphereBalanceEvent | null>(null);

  const clearLatestAuthor = useCallback(() => setLatestAuthor(null), []);
  const clearPhaseRecapReady = useCallback(() => setPhaseRecapReady(null), []);
  const clearNarrativeReady = useCallback(() => setNarrativeReady(null), []);
  const clearPhaseRecapTTSReady = useCallback(() => setPhaseRecapTTSReady(null), []);
  const clearPhaseRecapEnhanced = useCallback(() => setPhaseRecapEnhanced(null), []);
  const clearAdaptivePromptsReady = useCallback(() => setAdaptivePromptsReady(null), []);
  const clearNarrativeEnhanced = useCallback(() => setNarrativeEnhanced(null), []);
  const clearDeliverableTTSReady = useCallback(() => setDeliverableTTSReady(null), []);
  const clearDeliverableReady = useCallback(() => setDeliverableReady(null), []);
  const clearRecapRenderComplete = useCallback(() => setRecapRenderComplete(null), []);
  const clearRecapRenderFailed = useCallback(() => setRecapRenderFailed(null), []);
  const clearSphereBalance = useCallback(() => setSphereBalance(null), []);

  // Track the threadId in a ref so reconnect handler always sees the latest value
  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;
  const onTurnContextInvalidatedRef = useRef(options.onTurnContextInvalidated);
  onTurnContextInvalidatedRef.current = options.onTurnContextInvalidated;
  const onReactionAddedRef = useRef(options.onReactionAdded);
  onReactionAddedRef.current = options.onReactionAdded;
  const onReactionRemovedRef = useRef(options.onReactionRemoved);
  onReactionRemovedRef.current = options.onReactionRemoved;

  useEffect(() => {
    if (!threadId) return;

    let cleanedUp = false;

    const handleNewContribution = (data: { contribution: Contribution }) => {
      setLiveContributions(prev => [data.contribution, ...prev]);
      setLatestAuthor(data.contribution.authorId ?? 'Someone');
    };

    // CAPTURE_FIRST_STORY_FLOW — live banter (guests already join thread:{id},
    // so these events arrive with no extra subscription work).
    // Capture the active live-session id from any live:* event so a guest can
    // post (the 1 Hz live:snapshot ticker guarantees it arrives within ~1s).
    const captureSessionId = (sessionId?: string) => {
      if (sessionId) setLiveSessionId(prev => prev ?? sessionId);
    };

    const handleLiveSnapshot = (data: { sessionId?: string }) => {
      captureSessionId(data?.sessionId);
    };

    const handleLiveSessionEnded = () => {
      setLiveSessionId(null);
      setLiveMessages([]);
      setLiveReaction(null);
    };

    const handleLiveMessage = (data: {
      id: string;
      sessionId?: string;
      userId: string | null;
      guestSessionId: string | null;
      authorDisplayName: string;
      colorSeed: string;
      content: string;
    }) => {
      captureSessionId(data.sessionId);
      setLiveMessages(prev => {
        if (prev.some(m => m.id === data.id)) return prev;
        const next = [...prev, {
          id: data.id,
          authorDisplayName: data.authorDisplayName,
          colorSeed: data.colorSeed,
          content: data.content,
          isGuest: data.userId == null && data.guestSessionId != null,
        }];
        // Bound the retained set so a long, chatty session can't grow it without
        // limit (mirrors LiveReactionLayer's float-glyph cap).
        return next.length > LIVE_MESSAGE_CAP ? next.slice(-LIVE_MESSAGE_CAP) : next;
      });
    };

    const handleLiveMessageRemoved = (data: { messageId: string }) => {
      setLiveMessages(prev => prev.filter(m => m.id !== data.messageId));
    };

    const handleLiveReaction = (data: { sessionId?: string; userId: string; emoji: string }) => {
      captureSessionId(data.sessionId);
      reactionKeyRef.current += 1;
      setLiveReaction({ seed: data.userId, emoji: data.emoji, key: reactionKeyRef.current });
    };

    const handlePresenceUpdate = (data: PresenceEntry) => {
      setPresence(prev => {
        const filtered = prev.filter(p => p.userId !== data.userId);
        return [...filtered, data];
      });
    };

    const handleUserLeft = (data: { userId: string }) => {
      setPresence(prev => prev.filter(p => p.userId !== data.userId));
    };

    const handlePhaseRecapReady = (data: PhaseRecapReadyEvent) => {
      setPhaseRecapReady(data);
    };

    const handleNarrativeReady = (data: NarrativeReadyEvent) => {
      setNarrativeReady(data);
    };

    const handlePhaseRecapTTSReady = (data: PhaseRecapTTSReadyEvent) => {
      setPhaseRecapTTSReady(data);
    };

    const handlePhaseRecapEnhanced = (data: PhaseRecapEnhancedEvent) => {
      setPhaseRecapEnhanced(data);
    };

    const handleAdaptivePromptsReady = (data: AdaptivePromptsReadyEvent) => {
      setAdaptivePromptsReady(data);
    };

    const handleNarrativeEnhanced = (data: NarrativeEnhancedEvent) => {
      setNarrativeEnhanced(data);
    };

    const handleDeliverableTTSReady = (data: DeliverableTTSReadyEvent) => {
      setDeliverableTTSReady(data);
    };

    const handleDeliverableReady = (data: DeliverableReadyEvent) => {
      setDeliverableReady(data);
    };

    const handleRecapRenderComplete = (data: RecapRenderCompleteEvent) => {
      setRecapRenderComplete(data);
    };

    const handleRecapRenderFailed = (data: RecapRenderFailedEvent) => {
      setRecapRenderFailed(data);
    };

    const handleSphereBalance = (data: SphereBalanceEvent) => {
      // Only surface when the backend actually flagged an empty sphere. An empty
      // emptyRoles array means the story has every part covered — nothing to
      // invite, so we stay quiet rather than nudge for no reason.
      if (data && data.nudgeMessage && (data.emptyRoles?.length ?? 0) > 0) {
        setSphereBalance(data);
      }
    };

    const handleTurnContextInvalidated = () => {
      onTurnContextInvalidatedRef.current?.();
    };

    // Backend emits { reaction } on add and a flat shape on remove. Normalize
    // both to the page-facing event and hand off to the reconciler.
    const handleReactionAdded = (data: { reaction: Reaction }) => {
      const r = data?.reaction;
      if (!r?.contributionId || !r?.reactionType) return;
      onReactionAddedRef.current?.({
        contributionId: r.contributionId,
        reactionType: r.reactionType,
        userId: r.userId ?? null,
        guestSessionId: r.guestSessionId ?? null,
      });
    };

    const handleReactionRemoved = (data: ReactionRemovedEvent) => {
      if (!data?.contributionId || !data?.reactionType) return;
      onReactionRemovedRef.current?.({
        contributionId: data.contributionId,
        reactionType: data.reactionType,
        userId: data.userId ?? null,
      });
    };

    const handleReconnect = () => {
      // Rejoin thread room after socket reconnects (Socket.IO does NOT auto-rejoin rooms)
      const currentThreadId = threadIdRef.current;
      if (currentThreadId) {
        joinThread(currentThreadId);
      }
    };

    let attachedSocket: ReturnType<typeof getSocket> = null;

    function setupListeners(socket: ReturnType<typeof getSocket>) {
      // onSocketReady is persistent (fires for the current socket AND every
      // future instance), so guard against re-attaching to the same one.
      if (!socket || cleanedUp || attachedSocket === socket) return;
      attachedSocket = socket;

      if (threadId) joinThread(threadId);

      socket.on('new_contribution', handleNewContribution);
      socket.on('live:snapshot', handleLiveSnapshot);
      socket.on('live:session_ended', handleLiveSessionEnded);
      socket.on('live:message', handleLiveMessage);
      socket.on('live:message_removed', handleLiveMessageRemoved);
      socket.on('live:reaction', handleLiveReaction);
      socket.on('user_presence_updated', handlePresenceUpdate);
      socket.on('user_left', handleUserLeft);
      socket.on('phase_recap_ready', handlePhaseRecapReady);
      socket.on('narrative_ready', handleNarrativeReady);
      socket.on('phase_recap_tts_ready', handlePhaseRecapTTSReady);
      socket.on('phase_recap_enhanced', handlePhaseRecapEnhanced);
      socket.on('adaptive_prompts_ready', handleAdaptivePromptsReady);
      socket.on('narrative_enhanced', handleNarrativeEnhanced);
      socket.on('deliverable_tts_ready', handleDeliverableTTSReady);
      socket.on('deliverable:ready', handleDeliverableReady);
      socket.on('recap_render_complete', handleRecapRenderComplete);
      socket.on('recap_render_failed', handleRecapRenderFailed);
      socket.on('sphere:balance', handleSphereBalance);
      socket.on('reaction_added', handleReactionAdded);
      socket.on('reaction_removed', handleReactionRemoved);
      socket.on('turn_changed', handleTurnContextInvalidated);
      socket.on('phase_advanced', handleTurnContextInvalidated);
      socket.on('elder_rotated', handleTurnContextInvalidated);
      socket.on('connect', handleReconnect);
    }

    // Attach to the current socket (onSocketReady fires immediately when one
    // exists) and re-attach to every future instance (auth change / recreate).
    const cancelReady = onSocketReady((s) => {
      setupListeners(s);
    });

    return () => {
      cleanedUp = true;
      cancelReady();
      const socket = attachedSocket;
      if (socket) {
        socket.off('new_contribution', handleNewContribution);
        socket.off('live:snapshot', handleLiveSnapshot);
        socket.off('live:session_ended', handleLiveSessionEnded);
        socket.off('live:message', handleLiveMessage);
        socket.off('live:message_removed', handleLiveMessageRemoved);
        socket.off('live:reaction', handleLiveReaction);
        socket.off('user_presence_updated', handlePresenceUpdate);
        socket.off('user_left', handleUserLeft);
        socket.off('phase_recap_ready', handlePhaseRecapReady);
        socket.off('narrative_ready', handleNarrativeReady);
        socket.off('phase_recap_tts_ready', handlePhaseRecapTTSReady);
        socket.off('phase_recap_enhanced', handlePhaseRecapEnhanced);
        socket.off('adaptive_prompts_ready', handleAdaptivePromptsReady);
        socket.off('narrative_enhanced', handleNarrativeEnhanced);
        socket.off('deliverable_tts_ready', handleDeliverableTTSReady);
        socket.off('deliverable:ready', handleDeliverableReady);
        socket.off('recap_render_complete', handleRecapRenderComplete);
        socket.off('recap_render_failed', handleRecapRenderFailed);
        socket.off('sphere:balance', handleSphereBalance);
        socket.off('reaction_added', handleReactionAdded);
        socket.off('reaction_removed', handleReactionRemoved);
        socket.off('turn_changed', handleTurnContextInvalidated);
        socket.off('phase_advanced', handleTurnContextInvalidated);
        socket.off('elder_rotated', handleTurnContextInvalidated);
        socket.off('connect', handleReconnect);
        if (threadId) leaveThread(threadId);
      }
      setLiveContributions([]);
      setLiveMessages([]);
      setLiveReaction(null);
      setLiveSessionId(null);
      setPresence([]);
      setLatestAuthor(null);
      setPhaseRecapReady(null);
      setNarrativeReady(null);
      setPhaseRecapTTSReady(null);
      setPhaseRecapEnhanced(null);
      setAdaptivePromptsReady(null);
      setNarrativeEnhanced(null);
      setDeliverableTTSReady(null);
      setDeliverableReady(null);
      setRecapRenderComplete(null);
      setRecapRenderFailed(null);
      setSphereBalance(null);
    };
  }, [threadId]);

  return {
    liveContributions,
    liveMessages,
    liveReaction,
    liveSessionId,
    presence,
    latestAuthor,
    clearLatestAuthor,
    phaseRecapReady,
    clearPhaseRecapReady,
    narrativeReady,
    clearNarrativeReady,
    phaseRecapTTSReady,
    clearPhaseRecapTTSReady,
    phaseRecapEnhanced,
    clearPhaseRecapEnhanced,
    adaptivePromptsReady,
    clearAdaptivePromptsReady,
    narrativeEnhanced,
    clearNarrativeEnhanced,
    deliverableTTSReady,
    clearDeliverableTTSReady,
    deliverableReady,
    clearDeliverableReady,
    recapRenderComplete,
    clearRecapRenderComplete,
    recapRenderFailed,
    clearRecapRenderFailed,
    sphereBalance,
    clearSphereBalance,
  };
}
